import { useState } from "react";

/**
 * useToggle, but the value survives a reload via localStorage.
 *
 * Storage is treated as best-effort throughout: Safari private mode and
 * hardened browser settings make both reads and writes throw, and a UI
 * preference is never worth breaking the control it belongs to. Every access
 * falls back to in-memory state.
 */
export const usePersistedToggle = (key: string, initialValue: boolean = false) => {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      // Only "true"/"false" count; anything else (absent, corrupted, written by
      // an older build) falls back rather than coercing to a surprising value.
      if (stored === "true" || stored === "false") {
        return stored === "true";
      }
    } catch {
      // Storage unavailable -- use the caller's default.
    }
    return initialValue;
  });

  const toggleValue = () => {
    setValue((previousValue) => {
      const next = !previousValue;
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // Preference is not persisted this session; the toggle still works.
      }
      return next;
    });
  };

  return [value, toggleValue] as const;
};
